import { ClassDefinitionDto } from '@zorro/clients';
import { ALL_EMPLOYEES_CLASS_NAME } from '@zorro/common-utils';
import union from 'lodash/union';

const isValidCategory = (customClass?: string): customClass is string =>
  !!customClass && customClass !== ALL_EMPLOYEES_CLASS_NAME;

/**
 * Extracts unique custom class categories from class definitions for the current and next year.
 * Filters out "All Employees" as it's the default class and shouldn't be selectable.
 * Used for employee/applicant forms to populate custom class category select options.
 */
export function extractCustomClassCategories(
  classDefinitions: ClassDefinitionDto[],
  currentYear: number,
  shouldGetNextYear?: boolean
): string[] {
  if (!classDefinitions) return [];

  const currentYearDefs = classDefinitions.filter(
    (def: ClassDefinitionDto) => def.year === currentYear
  );

  const extractCustomClassCategories = (defs: ClassDefinitionDto[]) =>
    new Set(
      defs.flatMap((def) =>
        (def.items ?? [])
          .map((item) => item.customClassCategory)
          .filter(isValidCategory)
      )
    );

  const currentYearCategories = extractCustomClassCategories(currentYearDefs);

  if (shouldGetNextYear) {
    const nextYear = currentYear + 1;
    const nextYearDefs = classDefinitions.filter(
      (def: ClassDefinitionDto) => def.year === nextYear
    );
    const nextYearCategories = extractCustomClassCategories(nextYearDefs);

    if (nextYearCategories.size > 0) {
      return union(
        Array.from(currentYearCategories),
        Array.from(nextYearCategories)
      );
    }
  }

  return Array.from(currentYearCategories);
}
